import { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { ChevronDown, Menu, LogOut, ArrowRight, Inbox, PenSquare } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "../ui/sheet";
import { useAuth } from "../../context/AuthContext";
import { useLeadModal } from "../../context/LeadModalContext";
import { ChevronMark, Wordmark } from "../ChevronMark";
import { SERVICE_CATEGORIES, RESOURCE_CATEGORIES, PROGRAM_CATEGORIES } from "../../data/categories";

const linkClass = ({ isActive }) =>
  `text-sm font-medium transition-colors duration-200 ${
    isActive ? "text-forest" : "text-foreground/60 hover:text-forest"
  }`;

function NavDropdown({ label, items, testId }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        data-testid={testId}
        className="group flex items-center gap-1 text-sm font-medium text-foreground/60 outline-none transition-colors duration-200 hover:text-forest data-[state=open]:text-forest"
      >
        {label}
        <ChevronDown className="h-3.5 w-3.5 transition-transform duration-200 group-data-[state=open]:rotate-180" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" sideOffset={14} className="w-80 rounded-2xl border-border bg-white p-2 shadow-xl">
        {items.map((item) => (
          <DropdownMenuItem key={item.slug} asChild className="cursor-pointer rounded-xl px-3 py-3 focus:bg-secondary">
            <Link to={item.path} data-testid={`nav-link-${item.slug}`} className="flex items-start justify-between gap-3">
              <span>
                <span className="block text-sm font-semibold text-forest-deep">{item.name}</span>
                <span className="mt-0.5 block text-xs leading-snug text-muted-foreground">{item.tagline}</span>
              </span>
              <ArrowRight className="mt-1 h-3.5 w-3.5 shrink-0 text-gold-dark" />
            </Link>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user, logout } = useAuth();
  const { openLeadForm } = useLeadModal();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <header
      data-testid="site-navbar"
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,box-shadow,border-color] duration-300 ${
        scrolled
          ? "border-b border-forest/10 bg-bone/90 shadow-[0_4px_30px_rgba(10,41,30,0.06)] backdrop-blur-xl"
          : "border-b border-transparent bg-bone/60 backdrop-blur-md"
      }`}
    >
      <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3">
          <ChevronMark className="h-8 w-auto" />
          <Wordmark />
        </Link>

        <nav className="hidden items-center gap-7 lg:flex" data-testid="nav-desktop-menu">
          <NavLink to="/" end className={linkClass} data-testid="nav-link-home">
            Home
          </NavLink>
          <NavLink to="/about" className={linkClass} data-testid="nav-link-about">
            About
          </NavLink>
          <NavDropdown label="Services" items={SERVICE_CATEGORIES} testId="nav-services-dropdown" />
          <NavLink to="/tutors" className={linkClass} data-testid="nav-link-tutors">
            Tutors
          </NavLink>
          <NavLink to="/quranic-studies" className={linkClass} data-testid="nav-link-quranic-studies">
            Quranic Studies
          </NavLink>
          <NavDropdown label="Resources" items={RESOURCE_CATEGORIES} testId="nav-resources-dropdown" />
          <NavLink to="/training" className={linkClass} data-testid="nav-link-training">
            Training
          </NavLink>
          <NavLink to="/interview-prep" className={linkClass} data-testid="nav-link-interview-prep">
            Interview Prep
          </NavLink>
        </nav>

        <div className="hidden items-center gap-3 lg:flex">
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger data-testid="nav-user-menu" className="flex h-10 w-10 items-center justify-center rounded-full bg-forest text-sm font-bold text-bone outline-none">
                {(user.first_name?.[0] || user.name?.[0] || "U").toUpperCase()}
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 rounded-2xl border-border bg-white p-2 shadow-xl">
                <div className="px-3 py-2">
                  <p className="text-sm font-semibold text-forest-deep">{user.name}</p>
                  <p className="truncate text-xs text-muted-foreground">{user.email}</p>
                </div>
                {user.role === "admin" && (
                  <>
                    <DropdownMenuItem asChild className="cursor-pointer rounded-xl px-3 py-2.5 text-sm font-medium text-forest focus:bg-secondary">
                      <Link to="/admin/leads" data-testid="nav-admin-leads-link">
                        <Inbox className="mr-2 h-4 w-4" /> Leads Inbox
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild className="cursor-pointer rounded-xl px-3 py-2.5 text-sm font-medium text-forest focus:bg-secondary">
                      <Link to="/admin/content" data-testid="nav-admin-content-link">
                        <PenSquare className="mr-2 h-4 w-4" /> Content Editor
                      </Link>
                    </DropdownMenuItem>
                  </>
                )}
                <DropdownMenuItem
                  data-testid="nav-logout-btn"
                  onClick={handleLogout}
                  className="cursor-pointer rounded-xl px-3 py-2.5 text-sm text-destructive focus:bg-secondary"
                >
                  <LogOut className="mr-2 h-4 w-4" /> Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Link
              to="/login"
              data-testid="nav-login-link"
              className="text-sm font-medium text-foreground/60 transition-colors hover:text-forest"
            >
              Log in
            </Link>
          )}
          <button
            data-testid="nav-book-call-btn"
            onClick={() => openLeadForm("navbar")}
            className="group relative overflow-hidden rounded-full bg-gold px-5 py-2.5 font-display text-sm font-bold text-forest-deep transition-transform duration-200 hover:-translate-y-0.5 hover:shadow-[0_8px_25px_rgba(212,175,55,0.4)]"
          >
            Book a Free Call
          </button>
        </div>

        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <SheetTrigger asChild>
            <button data-testid="mobile-menu-btn" className="rounded-lg p-2 text-forest-deep lg:hidden" aria-label="Open menu">
              <Menu className="h-6 w-6" />
            </button>
          </SheetTrigger>
          <SheetContent side="right" className="w-[320px] overflow-y-auto bg-bone p-6">
            <SheetTitle className="sr-only">Navigation menu</SheetTitle>
            <div className="mt-2 flex items-center gap-3">
              <ChevronMark className="h-8 w-auto" />
              <Wordmark />
            </div>
            <nav className="mt-8 flex flex-col gap-1">
              <Link to="/" onClick={() => setMobileOpen(false)} data-testid="mobile-nav-home"
                className="rounded-xl px-3 py-3 font-display text-base font-semibold text-forest-deep hover:bg-secondary">
                Home
              </Link>
              <Link to="/about" onClick={() => setMobileOpen(false)} data-testid="mobile-nav-about"
                className="rounded-xl px-3 py-3 font-display text-base font-semibold text-forest-deep hover:bg-secondary">
                About
              </Link>
              <Link to="/tutors" onClick={() => setMobileOpen(false)} data-testid="mobile-nav-tutors"
                className="rounded-xl px-3 py-3 font-display text-base font-semibold text-forest-deep hover:bg-secondary">
                Tutors
              </Link>
              {[
                { label: "Services", items: SERVICE_CATEGORIES },
                { label: "Programs", items: PROGRAM_CATEGORIES },
                { label: "Resources", items: RESOURCE_CATEGORIES },
              ].map((group) => (
                <div key={group.label} className="mt-2">
                  <p className="px-3 font-mono text-[10px] uppercase tracking-[0.3em] text-gold-dark">{group.label}</p>
                  {group.items.map((item) => (
                    <Link key={item.slug} to={item.path} onClick={() => setMobileOpen(false)}
                      data-testid={`mobile-nav-${item.slug}`}
                      className="mt-0.5 block rounded-xl px-3 py-2.5 text-sm font-medium text-foreground/80 hover:bg-secondary">
                      {item.name}
                    </Link>
                  ))}
                </div>
              ))}
            </nav>
            <div className="mt-8 flex flex-col gap-3 border-t border-border pt-6">
              {user ? (
                <button onClick={() => { handleLogout(); setMobileOpen(false); }} data-testid="mobile-nav-logout"
                  className="rounded-full border border-border px-5 py-3 text-sm font-semibold text-foreground">
                  Log out ({user.first_name || user.name})
                </button>
              ) : (
                <Link to="/login" onClick={() => setMobileOpen(false)} data-testid="mobile-nav-login"
                  className="rounded-full border border-border px-5 py-3 text-center text-sm font-semibold text-foreground">
                  Log in
                </Link>
              )}
              <button
                data-testid="mobile-nav-book-call"
                onClick={() => { setMobileOpen(false); openLeadForm("mobile_nav"); }}
                className="rounded-full bg-gold px-5 py-3 font-display text-sm font-bold text-forest-deep"
              >
                Book a Free Call
              </button>
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </header>
  );
}
